<template>
  <v-app class="bg-white">
    <v-main class="auth-pages">
      <v-col cols="12" style="display: flex; justify-content: space-around;margin-top: 10px;">
        <img :height="this.$vuetify.breakpoint.xs ? 200 : 200" class="image-logo2" src="@/assets/img/logo.png">
      </v-col>
      <div class="policy-container container">
        <div class="header-section text-center mb-4">
          <h1>🏠 HomeSyncX™️</h1>
          <p class="contact-info">
            📧 <a href="mailto:support@homesyncx.com">support@homesyncx.com</a> | 🌐 <a href="https://www.homesyncx.com" target="_blank">www.homesyncx.com</a>
          </p>
        </div>

        <h1 class="text-center main-title">HomeSyncX™️ Compliance & Data Protection Statement</h1>
        <p class="last-updated text-center">Effective Date: October 26, 2025</p>

        <section>
          <h2>1. Purpose of Compliance</h2>
          <p>
            At HomeSyncX™️, operated by Round Tables LLC, we are committed to maintaining the highest standards of privacy, transparency, and legal compliance in all aspects of our business. This Compliance Statement outlines our adherence to state, federal, and international data protection laws as they relate to real estate, technology, and digital marketing operations.
          </p>
        </section>

        <section>
          <h2>2. Regulatory Compliance Framework</h2>
          <p>HomeSyncX™️ complies with the following key regulations and frameworks:</p>
          
          <h3>A. United States Regulations</h3>
          <ul>
            <li><strong>Fair Housing Act (FHA)</strong> — Prohibits discrimination in housing and real estate activities.</li>
            <li><strong>Equal Credit Opportunity Act (ECOA)</strong> — Ensures fair lending practices for all clients.</li>
            <li><strong>Gramm-Leach-Bliley Act (GLBA)</strong> — Protects sensitive financial and personal information.</li>
            <li><strong>California Consumer Privacy Act (CCPA)</strong> — Gives California residents control over their personal data.</li>
            <li><strong>CAN-SPAM Act</strong> — Regulates commercial emails and marketing communications.</li>
          </ul>

          <h3>B. International Standards</h3>
          <ul>
            <li><strong>General Data Protection Regulation (GDPR)</strong> — Applies to European Union citizens whose data is collected or processed.</li>
            <li><strong>Personal Information Protection and Electronic Documents Act (PIPEDA)</strong> — Applies to Canadian data protection standards.</li>
          </ul>
        </section>

        <section>
          <h2>3. Real Estate Industry Compliance</h2>
          <p>
            HomeSyncX™️ aligns with all real estate advertising and data privacy laws governing client information, lead distribution, and referral marketing, including:
          </p>
          <ul>
            <li>Maintaining non-discriminatory advertising in property listings and promotions.</li>
            <li>Ensuring all licensed agents and brokers adhere to state-specific disclosure requirements.</li>
            <li>Keeping referral commissions and earnings transparent under applicable real estate laws.</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Privacy and Security Practices</h2>
          <p>We employ industry-leading data protection protocols, including:</p>
          <ul>
            <li>SSL encryption for all website and application data transmissions.</li>
            <li>Two-factor authentication (2FA) for agent accounts and administrative access.</li>
            <li>Secure cloud-based data storage compliant with SOC 2 and ISO 27001 standards.</li>
            <li>Role-based access controls to limit internal data visibility.</li>
          </ul>
          <p>
            We never sell, lease, or trade user information. All data collected is used solely to improve services, verify referrals, and maintain compliance with real estate transaction tracking standards.
          </p>
        </section>

        <section>
          <h2>5. Partner and Vendor Compliance</h2>
          <p>
            All third-party partners and vendors who interact with HomeSyncX™️ data must meet equivalent or higher compliance standards, including:
          </p>
          <ul>
            <li>Executing Data Processing Agreements (DPAs)</li>
            <li>Adhering to GDPR and CCPA when applicable</li>
            <li>Maintaining confidentiality clauses for sensitive data</li>
          </ul>
          <p>Partners include:</p>
          <ul>
            <li>MLS data providers</li>
            <li>Mortgage lenders and credit unions</li>
            <li>Title and escrow companies</li>
            <li>Marketing automation and CRM providers</li>
          </ul>
        </section>

        <section>
          <h2>6. User Rights and Data Requests</h2>
          <p>All users and agents have the right to:</p>
          <ul>
            <li>Access and review their stored data</li>
            <li>Request corrections or deletion ("Right to be Forgotten")</li>
            <li>Withdraw consent for marketing communications</li>
            <li>Obtain a copy of their data in a portable format (where applicable)</li>
          </ul>
          <p>Requests can be made by contacting:</p>
          <p>
            📧 <a href="mailto:privacy@homesyncx.com">privacy@homesyncx.com</a>
          </p>
          <p>
            All verified requests are processed within 30 business days in accordance with CCPA and GDPR timelines.
          </p>
        </section>

        <section>
          <h2>7. Breach Response & Incident Reporting</h2>
          <p>In the unlikely event of a data breach or unauthorized access, HomeSyncX™️ will:</p>
          <ul>
            <li>Notify affected users within 72 hours of detection (as required by GDPR)</li>
            <li>Investigate, contain, and document the incident</li>
            <li>Report findings to applicable regulatory authorities if required by law</li>
          </ul>
        </section>

        <section>
          <h2>8. Record Retention</h2>
          <p>
            All real estate transaction and referral data is retained for a minimum of 7 years in accordance with federal and state real estate laws, unless deletion is requested or legally mandated.
          </p>
        </section>

        <section>
          <h2>9. Ongoing Review and Audit</h2>
          <p>
            HomeSyncX™️ conducts regular compliance audits and employee training to ensure continuous alignment with evolving privacy, advertising, and technology regulations.
          </p>
        </section>

        <section>
          <h2>10. Contact Compliance</h2>
          <p>For compliance, privacy, or legal inquiries:</p>
          <p>
            📧 <a href="mailto:compliance@homesyncx.com">compliance@homesyncx.com</a><br>
            🏢 HomeSyncX™️ | Round Tables LLC<br>
            931 N Dysart Rd, Suite B, Avondale, AZ 85323
          </p>
        </section>

        <section>
          <h2>Final Declaration</h2>
          <p>
            By accessing the HomeSyncX™️ platform, users acknowledge and agree that HomeSyncX™️ operates in full compliance with applicable data protection and real estate laws and that the platform upholds fairness, transparency, and accountability across all systems and partnerships.
          </p>
        </section>

        <div class="back-link-section text-center mt-8">
          <v-btn color="#0052CC" dark @click="$router.push('/')">
            <i class="material-icons-round mr-2">arrow_back</i>
            Back to Home
          </v-btn>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'Compliance',
};
</script>

<style scoped>
.policy-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.7;
  color: #333;
}

.header-section {
  margin-bottom: 30px;
  padding: 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 15px;
}

.header-section h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #0052CC;
  margin-bottom: 10px;
}

.contact-info {
  font-size: 1rem;
  color: #666;
  margin-top: 10px;
}

.contact-info a {
  color: #0052CC;
  text-decoration: none;
}

.contact-info a:hover {
  text-decoration: underline;
}

.main-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 10px;
}

.last-updated {
  font-size: 0.95rem;
  color: #666;
  margin-bottom: 40px;
  font-style: italic;
}

section {
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid #e0e0e0;
}

section:last-of-type {
  border-bottom: none;
}

h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #0052CC;
  margin-bottom: 20px;
  margin-top: 0;
}

h3 {
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a2e;
  margin-top: 25px;
  margin-bottom: 15px;
}

p {
  margin-bottom: 15px;
  font-size: 1rem;
  color: #444;
}

ul {
  margin: 15px 0;
  padding-left: 30px;
}

li {
  margin-bottom: 10px;
  font-size: 1rem;
  color: #444;
}

a {
  color: #0052CC;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

strong {
  font-weight: 600;
  color: #1a1a2e;
}

.back-link-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 2px solid #e0e0e0;
}

@media (max-width: 768px) {
  .policy-container {
    padding: 20px 15px;
  }

  .header-section h1 {
    font-size: 1.8rem;
  }

  .main-title {
    font-size: 1.5rem;
  }

  h2 {
    font-size: 1.4rem;
  }

  h3 {
    font-size: 1.1rem;
  }
}
</style>

